import { Logo } from '@/components/logo'
import { OnboardingLayout } from '@/components/onboarding-layout'
import { Button, EmailInput, Group, Field, Notice } from '@tempots/beatui'
import {
  attr,
  computedOf,
  Empty,
  Fragment,
  html,
  OneOfValue,
  prop,
  Use,
} from '@tempots/dom'
import { Anchor, HTMLTitle, Location } from '@tempots/ui'
import { ApiError, Auth } from '@/services/auth-service'
import { authState, setPendingVerificationEmail } from '@/stores/auth-store'
import { Track } from '@/services/track'

export function VerifyEmailPage() {
  return Fragment(
    HTMLTitle('Verify Email - ResumeHeaders'),
    Use(Location, location => {
      const initialEmail = computedOf(
        location.search.$.email,
        authState.$.pendingVerificationEmail
      )((locationEmail, pendingEmail) => {
        return locationEmail ?? pendingEmail ?? ''
      })
      const email = initialEmail.deriveProp()
      const sending = prop(false)
      const feedbackState = prop<'idle' | 'success' | 'error'>('idle')
      const feedbackMessage = prop<string | null>(null)

      const handleResend = async () => {
        Track.click({
          section: 'main',
          element_type: 'button',
          click_action: 'submit',
          element_id: 'resend_verification',
        })

        const value = email.value.trim()

        if (!value) {
          feedbackState.set('error')
          feedbackMessage.set(
            'Enter the email you used to sign up before requesting a new link.'
          )
          Track.clientError({
            error_code: 'VERIFY_EMAIL_EMPTY_EMAIL',
            error_message: 'Email is required',
          })
          return
        }

        try {
          sending.set(true)
          await Auth.resendVerification(value)
          Track.auth({
            action: 'email_verification_resend',
            user_id: authState.value.user?.id,
            message: 'Email verification resend requested',
          })
          setPendingVerificationEmail(value)
          feedbackState.set('success')
          feedbackMessage.set(
            'If the account is still pending verification, a new confirmation email is on its way.'
          )
        } catch (error) {
          feedbackState.set('error')
          Track.apiError({
            error_code: 'EMAIL_VERIFICATION_RESEND_FAILED',
            error_message:
              error instanceof ApiError ? error.message : 'Unknown error',
          })
          if (error instanceof ApiError) {
            feedbackMessage.set(
              error.message ||
                'We could not send a new verification email right now. Please try again later.'
            )
          } else {
            feedbackMessage.set(
              'We could not send a new verification email right now. Please try again later.'
            )
          }
        } finally {
          sending.set(false)
        }
      }

      Auth.emailVerified.on(verified => {
        if (verified) {
          location.navigate('/start', { replace: true })
        }
      })

      return OnboardingLayout(
        {},
        html.div(
          attr.class('w-full md:max-w-md'),
          html.div(
            attr.class('flex justify-center mb-6 md:mb-8'),
            Anchor({ href: '/' }, Logo({ size: 'main' }))
          ),
          html.h2(
            attr.class('text-lg md:text-xl font-bold text-center mb-3 md:mb-4'),
            'Confirm your email address'
          ),
          html.p(
            attr.class('text-center text-gray-600 mb-0 text-sm md:text-base'),
            'We sent a confirmation link to your inbox.'
          ),
          html.p(
            attr.class('text-center text-gray-600 mb-6 text-sm md:text-base'),
            'Open the link in that email to activate your account and continue.'
          ),
          html.div(
            attr.class('flex flex-col gap-4'),
            Field({
              label: 'Email address',
              content: html.div(
                attr.id('email-input'),
                EmailInput({
                  value: email,
                  onChange: v => email.set(v),
                })
              ),
            }),
            html.div(
              attr.id('submit-button'),
              Button(
                {
                  variant: 'filled',
                  color: 'primary',
                  disabled: sending,
                  loading: sending,
                  onClick: handleResend,
                },
                Group(
                  attr.class('items-center gap-2 justify-center'),
                  html.span('Resend confirmation email')
                )
              )
            ),
            OneOfValue(feedbackState, {
              success: () =>
                html.div(
                  attr.id('success-message'),
                  Notice(
                    {
                      variant: 'success',
                      tone: 'prominent',
                      title: 'Email sent',
                    },
                    'If the account is still pending verification, a new confirmation email is on its way.'
                  )
                ),
              error: () =>
                html.div(
                  attr.id('error-message'),
                  Notice(
                    {
                      variant: 'danger',
                      tone: 'prominent',
                      title: 'Error',
                    },
                    feedbackMessage.map(msg => msg ?? '')
                  )
                ),
              idle: () => Empty,
            })
          )
        )
      )
    })
  )
}
